import { useEffect, useState } from 'react';
import clsx from 'clsx';

/**
 * Çağrı türlerini segmente bölen donut chart. Hedef koymadığımız için
 * absolute progress yok — sadece relative oran. Her segment kendi
 * rengindeki dolu arc, geri kalan boşluk slate-100.
 *
 * - Mount'tan sonra animasyonla dolar (CSS transition on stroke-dashoffset)
 * - Hiç çağrı yoksa "Henüz çağrı yok" empty state
 * - Center: toplam çağrı sayısı
 *
 * Implementation: tek SVG, 3 ayrı circle. Her circle'ın strokeDasharray
 * uzunluğu = (segmentValue / total) * circumference. offset birikerek arttırılır.
 */
export interface DonutSegment {
  label: string;
  value: number;
  /** Tailwind text-* class (örn 'text-emerald-500'). */
  colorClass: string;
}

/**
 * iPhone "Activity Rings" tarzı — 3 eş-merkezli halka (her segment bir halka),
 * yuvarlak uçlu, kendi renginde. Her halka segment'in toplam içindeki payını
 * (value/total) doldurur. Hedef yok — relative oran. Merkez: toplam çağrı.
 * Altındaki cevap/cevapsız/giden legend (DonutLegend) ayrı render edilir.
 */
export function CallDonut({ segments, size = 200 }: { segments: DonutSegment[]; size?: number }) {
  const total = segments.reduce((a, b) => a + b.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const ringStroke = Math.max(8, Math.round(size * 0.085));
  const gap = Math.round(ringStroke * 0.5);
  const outerR = (size - ringStroke) / 2;

  // Animasyon — başta 0, sonra her halka kendi oranına dolar
  const [animated, setAnimated] = useState<number[]>(() => segments.map(() => 0));
  useEffect(() => {
    const id = setTimeout(() => {
      setAnimated(segments.map((s) => (total > 0 ? s.value / total : 0)));
    }, 60);
    return () => clearTimeout(id);
  }, [segments, total]);

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        {segments.map((s, i) => {
          const r = outerR - i * (ringStroke + gap);
          if (r <= ringStroke) return null; // halka sığmıyorsa atla
          const circ = 2 * Math.PI * r;
          const frac = animated[i] ?? 0;
          return (
            <g key={i} className={s.colorClass}>
              {/* Faint track — halkanın boş kısmı (kendi renginde, soluk) */}
              <circle
                cx={cx} cy={cy} r={r} fill="none"
                stroke="currentColor" strokeOpacity={0.14} strokeWidth={ringStroke}
              />
              {/* Dolu arc — value/total, yuvarlak uç (Apple stili) */}
              {total > 0 && frac > 0 && (
                <circle
                  cx={cx} cy={cy} r={r} fill="none"
                  stroke="currentColor" strokeWidth={ringStroke}
                  strokeDasharray={`${frac * circ} ${circ}`}
                  strokeLinecap="round"
                  className="transition-[stroke-dasharray] duration-[1100ms] ease-out"
                />
              )}
            </g>
          );
        })}
      </svg>
      {/* Merkez: toplam çağrı */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        {total === 0 ? (
          <div className="text-center">
            <div className="font-bold text-slate-300 tabular-nums leading-none" style={{ fontSize: Math.round(size * 0.18) }}>0</div>
            <div className="text-[8.5px] font-medium uppercase tracking-widest text-slate-400 mt-0.5">henüz yok</div>
          </div>
        ) : (
          <div className="text-center">
            <div className="font-bold text-slate-900 tabular-nums leading-none" style={{ fontSize: Math.round(size * 0.155) }}>{total}</div>
            <div className="text-[8px] font-medium uppercase tracking-widest text-slate-400 mt-0.5">çağrı</div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Donut altında render edilecek legend — her segment için mini-stat tile:
 *  ust noktada renk dot, büyük renkli sayı, alt küçük uppercase label.
 *  3-kolonlu grid, sayılar tile'lar arasında hizalı.
 *
 *  Tasarım notu: sayıyı segment renginde tutarak ring'le görsel bağ kurar
 *  (Apple Activity Rings legend pattern'ı). 0 değer dahil aynı renkte —
 *  visual consistency için.
 */
export function DonutLegend({ segments }: { segments: DonutSegment[] }) {
  return (
    <div className="grid grid-cols-3 gap-1 mt-4 w-full max-w-[260px]">
      {segments.map((s) => {
        const dotCls = s.colorClass.replace('text-', 'bg-');
        const isZero = s.value === 0;
        return (
          <div key={s.label} className="text-center px-1.5 py-1">
            <span className={clsx('w-1.5 h-1.5 rounded-full inline-block mb-1.5', dotCls, isZero && 'opacity-40')} />
            <div className={clsx(
              'text-[26px] font-bold tabular-nums leading-none mb-1 tracking-tight',
              isZero ? 'text-slate-300' : s.colorClass,
            )}>
              {s.value}
            </div>
            <div className="text-[9.5px] font-semibold uppercase tracking-widest text-slate-500">
              {s.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
